import { uid } from '../../../../lib/uid';

/**
 * Test data backing the Autogenerate button on the Investment Planning catalog forms.
 *
 * Each fills every REQUIRED field so the primary action enables on the first try, and the
 * optional ones the form can offer a real value for — a record generated with only its
 * required fields produces a profile that is a column of em dashes, which exercises nothing.
 *
 * Keep them in lockstep with their forms: a new required field must be added here in the same
 * change, or Autogenerate silently stops working.
 *
 * **Values are synthetic.** These are generic Philippine public-sector vocabulary — the sector
 * names an investment programme conventionally uses, a UACS-shaped code — not a real
 * organization's list copied from production. None of them lands in `rsys_pii`, so plausible
 * vocabulary is right here: the rule about obviously-synthetic values is about where a value
 * LANDS, and a sector name never leaves `rsys_`.
 */

const SECTORS = ['Social', 'Economic', 'Infrastructure', 'General Public Services', 'Environment'];

const SUB_SECTORS = [
  'Health',
  'Education',
  'Social Welfare',
  'Agriculture',
  'Transport',
  'Water and Sanitation',
  'Peace and Order',
];

const GOALS = [
  'Improve access to primary care',
  'Raise functional literacy',
  'Expand farm-to-market roads',
  'Strengthen disaster preparedness',
  'Widen potable water coverage',
];

const OBJECTIVES = [
  'Reduce the average travel time to the nearest facility',
  'Increase the proportion of households served within the year',
  'Bring service coverage to every barangay in the locality',
  'Cut the backlog carried over from the previous year',
];

const STRATEGIES = [
  'Procure and deploy additional units across underserved areas',
  'Partner with national agencies for counterpart funding',
  'Train and deploy frontline personnel to the areas with the widest gap',
];

const ACCOUNT_TITLES = [
  'Office Supplies Expenses',
  'Travelling Expenses',
  'Training Expenses',
  'Repairs and Maintenance',
  'Fuel, Oil and Lubricants',
  'Water Expenses',
  'Electricity Expenses',
];

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

/** Six characters is enough to make a repeated click produce a name the server accepts. */
function suffix(): string {
  return uid().slice(0, 6).toLowerCase();
}

/**
 * One sector.
 *
 * The name carries a suffix because it is unique among the ACTIVE rows, and Autogenerate is
 * most useful clicked repeatedly — without one the second click would produce a name the
 * server returns the FIRST row for, which reads as the button having done nothing.
 */
export function sampleGoalSector(): { name: string; description: string } {
  return {
    name: `${pick(SECTORS)} ${suffix()}`,
    description: 'Generated for testing. Replace with what this sector actually covers.',
  };
}

/**
 * One sub-sector.
 *
 * The SECTOR is deliberately absent: it is a field the reader has already decided, either by
 * choosing it in the form or by having it locked from the row they opened the form on.
 * Generating over it would fight the form rather than fill it.
 */
export function sampleGoalSubSector(): { name: string; description: string } {
  return {
    name: `${pick(SUB_SECTORS)} ${suffix()}`,
    description: 'Generated for testing. Replace with what this sub-sector covers.',
  };
}

/** One goal, with the objective it serves and a strategy for reaching it. */
export function sampleGoal(): { name: string; objective: string; strategy: string } {
  return {
    name: `${pick(GOALS)} ${suffix()}`,
    objective: pick(OBJECTIVES),
    strategy: pick(STRATEGIES),
  };
}

/**
 * One account code, in the UACS shape.
 *
 * The code carries the random part rather than a suffix appended to a real one: a UACS code is
 * a structured identifier, and bolting six characters onto the end of "5-02-03-010" would
 * produce something that is not one.
 */
export function sampleBudgetAccountCode(): {
  code: string;
  acctNo: string;
  description: string;
} {
  const group = 2 + Math.floor(Math.random() * 4);
  const sub = 1 + Math.floor(Math.random() * 20);
  const item = 1 + Math.floor(Math.random() * 990);

  return {
    code: `5-0${group}-${String(sub).padStart(2, '0')}-${String(item).padStart(3, '0')}`,
    acctNo: String(1000 + Math.floor(Math.random() * 8999)),
    description: pick(ACCOUNT_TITLES),
  };
}
